import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, distinctUntilChanged, map, of, switchMap, throwError } from 'rxjs';

import { PreferencesResponse } from './tenant-admin-api.service';
import { PreferencesService } from './preferences.service';

/** Operator theme preference. '' = no explicit choice (follow the system). */
export type ThemePreference = '' | 'light' | 'dark';

/**
 * ThemeService — applies light/dark to admin-ui by setting the CSS
 * `color-scheme` property as an inline style on `document.body`.
 *
 * Story 29.2 rework: the preference lives in the server-side preference map
 * (PreferencesService, key "theme") — the former localStorage device cache is
 * GONE by operator decision (preferences must follow the operator across
 * devices; no split-brain between device cache and backend). Consequence:
 * before sign-in (and until the post-sign-in load lands) the UI follows the
 * system scheme; the operator's choice applies the moment the map loads.
 *
 * Why inline-on-body: the shared `mat.theme()` scaffold
 * (projects/shared/styles/custom-theme.scss) already emits `light-dark()`
 * color tokens for every `--mat-sys-*` variable; which side renders is driven
 * entirely by `color-scheme`. The shared stylesheet pins `body { color-scheme:
 * light; }`. Setting an inline style on `<body>` overrides that by specificity
 * — scoped to admin-ui's own document (custom-theme.scss stays UNTOUCHED, so
 * brand-site/emjo-login are unaffected). CDK overlays (dialogs, snackbars,
 * menus) live inside `<body>` and inherit, satisfying AC1's document-root reach.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly prefs = inject(PreferencesService);

  // sessionStorage key for the fast-render hint (NOT localStorage — hint is
  // intentionally per-tab; no cross-device split-brain risk).
  private static readonly HINT_KEY = 'theme_hint';

  // Single source of truth for the currently-rendered dark state.
  // null = "no explicit preference set yet — use OS media query".
  // init() seeds this from the sessionStorage hint so isDark$ agrees with the
  // body.style.colorScheme applied before any backend preferences have loaded.
  private readonly _isDark = new BehaviorSubject<boolean | null>(null);

  /**
   * Applies a preference to the document. '' → 'light dark' (system-follow:
   * browsers render light when the OS expresses no dark preference → AC4
   * fallback); 'light'/'dark' pin the respective scheme.
   *
   * Explicit preferences are persisted to sessionStorage so the next page
   * load within the same browser session can fast-render the correct theme
   * before the backend preference arrives.
   */
  apply(pref: ThemePreference): void {
    document.body.style.colorScheme = pref === '' ? 'light dark' : pref;
    this._isDark.next(pref === '' ? null : pref === 'dark');
    try {
      if (pref !== '') {
        sessionStorage.setItem(ThemeService.HINT_KEY, pref);
      } else {
        sessionStorage.removeItem(ThemeService.HINT_KEY);
      }
    } catch { /* ignore — sessionStorage unavailable */ }
  }

  /**
   * Wires the document to the preference map: applies any fast-render hint
   * immediately, then re-applies on every map emission (the post-sign-in
   * load, setAndPersist round-trips, and error reverts all flow through
   * here). Called once at app bootstrap (AppComponent).
   */
  init(): void {
    // Read the sessionStorage hint so the pre-login window uses the last
    // known preference rather than the OS default. The initial BehaviorSubject
    // emission (empty map, before any backend load) would immediately override
    // the hint with system-follow, so we skip it when a hint is active.
    let skipInitial = false;
    try {
      const h = sessionStorage.getItem(ThemeService.HINT_KEY);
      if (h === 'light' || h === 'dark') {
        document.body.style.colorScheme = h;
        // Seed _isDark so isDark$ reflects the hint immediately — before the
        // prefs map loads. Without this, isDark$ would fall through to the OS
        // media query and show the wrong logo when OS ≠ stored preference.
        this._isDark.next(h === 'dark');
        skipInitial = true;
      }
    } catch { /* ignore */ }

    let first = true;
    this.prefs.changes$.subscribe((prefs) => {
      if (first && skipInitial) { first = false; return; }
      first = false;
      this.apply(this.normalize(prefs['theme'] ?? ''));
    });
  }

  /**
   * Triggers the per-session preference-map load (sidebar shell, post-auth).
   * Application happens via the init() subscription. Silent on error — theme
   * is non-critical, so a failed fetch keeps the system appearance.
   */
  loadFromBackend(): void {
    this.prefs.load();
  }

  /**
   * Optimistically applies the theme, then persists it through the preference
   * map. The direct apply() makes the change instant even before init()'s
   * subscription fires (and keeps the service self-contained); on error the
   * previous theme is re-applied, the map reverts, and the error is rethrown
   * so the caller can surface a snackbar.
   */
  setAndPersist(theme: 'light' | 'dark'): Observable<PreferencesResponse> {
    const previous = this.normalize(this.prefs.get('theme') ?? '');
    this.apply(theme);
    return this.prefs.set('theme', theme).pipe(
      catchError((err) => {
        this.apply(previous);
        return throwError(() => err);
      }),
    );
  }

  /** The current normalized preference from the (possibly not-yet-loaded) map. */
  current(): Observable<ThemePreference> {
    return this.prefs.changes$.pipe(map((p) => this.normalize(p['theme'] ?? '')));
  }

  /**
   * Emits true when the currently rendered scheme is dark, false when light.
   * Driven by _isDark, which is seeded in init() from the sessionStorage hint
   * and updated by every apply() call. For the system-follow case (null),
   * falls through to real-time OS matchMedia so the logo tracks the OS when
   * the operator has expressed no explicit preference.
   */
  isDark$: Observable<boolean> = this._isDark.pipe(
    switchMap((explicit) => {
      if (explicit !== null) return of(explicit);
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      return new Observable<boolean>((sub) => {
        sub.next(mql.matches);
        const fn = (e: MediaQueryListEvent) => sub.next(e.matches);
        mql.addEventListener('change', fn);
        return () => mql.removeEventListener('change', fn);
      });
    }),
    distinctUntilChanged(),
  );

  normalize(theme: string): ThemePreference {
    return theme === 'light' || theme === 'dark' ? theme : '';
  }
}
